import * as React from 'react';
import { supabase, supabaseAdmin } from '../services/supabaseClient';
import { User, Session, AuthChangeEvent, UserIdentity } from '@supabase/supabase-js';

// Define the CustomUser type with all required User properties
interface CustomUser extends Omit<User, 'phone' | 'identities' | 'confirmed_at' | 'email_confirmed_at' | 'phone_confirmed_at' | 'last_sign_in_at'> {
  name: string | null;
  role: string;
  phone: string | null;
  confirmed_at: string | null;
  email_confirmed_at: string | null;
  phone_confirmed_at: string | null;
  last_sign_in_at: string | null;
  achievements?: {
    [key: string]: {
      unlocked: boolean;
      unlockedAt?: string;
      progress: number;
    };
  };
  app_metadata: {
    provider?: string;
    providers?: string[];
  };
  user_metadata: {
    name?: string;
    role?: string;
  };
  identities: Array<{
    id: string;
    user_id: string;
    identity_data: {
      [key: string]: any;
      email?: string;
      sub?: string;
    };
    provider: string;
    created_at: string;
    updated_at: string;
    last_sign_in_at: string;
  }>;
}

interface RegistrationResult {
  success: boolean;
  message: string;
  errorType?: 'user_exists' | 'weak_password' | 'invalid_email' | 'other';
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: CustomUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<RegistrationResult>;
  signInWithProvider: (provider: 'google' | 'github' | 'facebook' | 'linkedin_oidc' | 'discord' | 'twitter') => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  demoLogin: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider component that manages user authentication state
 * Provides login, register, and logout functionality to the application
 * @param children - React components that will have access to the auth context
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => {
    const stored = localStorage.getItem('isAuthenticated');
    return stored === 'true';
  });

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [user, setUser] = React.useState<CustomUser | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const clearError = () => setError(null);

  /**
   * Persists authentication state and user data to localStorage
   * whenever they change
   */
  React.useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      // Check if we have a demo user stored
      const storedUserStr = localStorage.getItem('user');
      if (storedUserStr) {
        try {
          const storedUser = JSON.parse(storedUserStr);
          if (storedUser.id === 'demo-user-123') {
            // It's a demo user, don't check Supabase and keep session active
            setUser(storedUser);
            setIsAuthenticated(true);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error('Error parsing stored user', e);
        }
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const customUser: CustomUser = {
            ...user,
            name: user.user_metadata?.name || null,
            role: user.user_metadata?.role || 'user',
            phone: user.phone || null,
            confirmed_at: user.confirmed_at || null,
            email_confirmed_at: user.email_confirmed_at || null,
            phone_confirmed_at: user.phone_confirmed_at || null,
            last_sign_in_at: user.last_sign_in_at || null,
            app_metadata: user.app_metadata || {},
            user_metadata: user.user_metadata || {},
            identities: (user.identities || []).map(identity => ({
              id: identity.id,
              user_id: identity.user_id,
              identity_data: identity.identity_data || { email: undefined, sub: undefined },
              provider: identity.provider,
              created_at: identity.created_at || user.created_at,
              updated_at: identity.updated_at || identity.created_at || user.created_at,
              last_sign_in_at: identity.last_sign_in_at || user.last_sign_in_at || user.created_at
            }))
          };
          setUser(customUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event: AuthChangeEvent, session: Session | null) => {
        if (session?.user) {
          const customUser: CustomUser = {
            ...session.user,
            name: session.user.user_metadata?.name || null,
            role: session.user.user_metadata?.role || 'user',
            phone: session.user.phone || null,
            confirmed_at: session.user.confirmed_at || null,
            email_confirmed_at: session.user.email_confirmed_at || null,
            phone_confirmed_at: session.user.phone_confirmed_at || null,
            last_sign_in_at: session.user.last_sign_in_at || null,
            app_metadata: session.user.app_metadata || {},
            user_metadata: session.user.user_metadata || {},
            identities: (session.user.identities || []).map(identity => ({
              id: identity.id,
              user_id: identity.user_id,
              identity_data: identity.identity_data || { email: undefined, sub: undefined },
              provider: identity.provider,
              created_at: identity.created_at || session.user.created_at,
              updated_at: identity.updated_at || identity.created_at || session.user.created_at,
              last_sign_in_at: identity.last_sign_in_at || session.user.last_sign_in_at || session.user.created_at
            }))
          };
          setUser(customUser);
          setIsAuthenticated(true);
        } else {
          // Check if currently logged in as demo user before clearing
          const storedUserStr = localStorage.getItem('user');
          if (storedUserStr) {
            try {
              const storedUser = JSON.parse(storedUserStr);
              if (storedUser.id === 'demo-user-123') {
                // Keep demo user logged in
                return;
              }
            } catch (e) {
              // Ignore error
            }
          }

          setUser(null);
          setIsAuthenticated(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    localStorage.setItem('isAuthenticated', isAuthenticated.toString());
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [isAuthenticated, user]);

  // Helper to ensure a user profile exists in the profiles table
  async function ensureUserProfile(user: any) {
    if (!user) return;
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (!profile) {
      await supabase.from('profiles').insert([{
        id: user.id,
        email: user.email,
        name: user.user_metadata?.name || '',
        role: user.user_metadata?.role || 'farmer',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }]);
    }
  }

  /**
   * Authenticates a user with their email and password
   * @param email - The user's email address
   * @param password - The user's password
   * @returns Promise that resolves when login is complete
   */
  const login = async (email: string, password: string) => {
    setError(null);
    // Check for demo credentials
    if (email === 'free@demo.com' && password === 'password123') {
      await demoLogin();
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        if (error.message.includes('Email not confirmed')) {
          const msg = 'Please verify your email before logging in. Check your inbox for the verification link.';
          setError(msg);
          throw new Error(msg);
        }
        setError(error.message);
        throw error;
      }
      if (data.user) {
        await ensureUserProfile(data.user);
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .maybeSingle();
        const customUser: CustomUser = {
          ...data.user,
          name: profile?.name || null,
          role: profile?.role || 'user',
          phone: data.user.phone || null,
          confirmed_at: data.user.confirmed_at || null,
          email_confirmed_at: data.user.email_confirmed_at || null,
          phone_confirmed_at: data.user.phone_confirmed_at || null,
          last_sign_in_at: data.user.last_sign_in_at || null,
          app_metadata: data.user.app_metadata || {},
          user_metadata: data.user.user_metadata || {},
          identities: (data.user.identities || []).map(identity => ({
            id: identity.id,
            user_id: identity.user_id,
            identity_data: identity.identity_data || { email: undefined, sub: undefined },
            provider: identity.provider,
            created_at: identity.created_at || data.user.created_at,
            updated_at: identity.updated_at || identity.created_at || data.user.created_at,
            last_sign_in_at: identity.last_sign_in_at || data.user.last_sign_in_at || data.user.created_at
          }))
        };
        setIsAuthenticated(true);
        setUser(customUser);
        // Navigation handled by calling component
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      if (!error.message) {
        setError('Login failed');
      }
      throw error;
    }
  };

  /**
   * Authenticates a user with a third-party provider (Google, GitHub, etc.)
   * @param provider - The OAuth provider to use
   */
  const signInWithProvider = async (provider: 'google' | 'github' | 'facebook' | 'linkedin_oidc' | 'discord' | 'twitter') => {
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) throw error;
    } catch (error: any) {
      console.error(`${provider} login failed:`, error);
      setError(error.message || `${provider} login failed`);
      throw error;
    }
  };

  /**
   * Registers a new user with the provided information
   * @param name - The user's name
   * @param email - The user's email address
   * @param password - The user's chosen password
   * @returns Promise that resolves when registration is complete
   */
  const register = async (name: string, email: string, password: string): Promise<RegistrationResult> => {
    setError(null);
    try {
      // First, sign up the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
          options: {
            data: {
              name,
            },
            // After registration send the confirmation email to the verification
            // page. That page will confirm the token and close the tab so users
            // don't land on a blank Supabase page.
            emailRedirectTo: `${window.location.origin}/verify-email`,
          },
      });

      if (authError) {
        let msg = authError.message;
        let errType: RegistrationResult['errorType'] = 'other';

        // Handle specific error cases
        if (authError.message.includes('User already registered')) {
          msg = 'An account with this email already exists. Would you like to reset your password?';
          errType = 'user_exists';
        } else if (authError.message.includes('Password should be at least 6 characters')) {
          msg = 'Password should be at least 6 characters long';
          errType = 'weak_password';
        } else if (authError.message.includes('Invalid email')) {
          msg = 'Please enter a valid email address';
          errType = 'invalid_email';
        }

        setError(msg);
        return {
          success: false,
          message: msg,
          errorType: errType
        };
      }

      if (authData.user) {
        // Ensure user profile exists after registration
        await ensureUserProfile(authData.user);
        // If supabaseAdmin is not initialized, still return success if user was created and confirmation email sent
        if (!supabaseAdmin) {
          // Comment: User was registered and confirmation email sent, but profile was not created in DB
          return {
            success: true,
            message: 'Registration successful! Please check your email to verify your account. (Profile creation pending)',
          };
        }
        // Create the user profile using the admin client
        const { error: profileError } = await supabaseAdmin
          .from('profiles')
          .insert([
            {
              id: authData.user.id,
              email,
              name,
              role: 'farmer',
            },
          ]);

        if (profileError) {
          console.error('Profile creation error:', profileError);
          // User is registered, but profile creation failed
          return {
            success: true,
            message: 'Registration successful! Please check your email to verify your account. (Profile creation failed, contact support if you have issues logging in.)',
          };
        }

        return {
          success: true,
          message: 'Registration successful! Please check your email to verify your account.',
        };
      }

      setError('Registration failed. Please try again.');
      throw new Error('Registration failed. Please try again.');
    } catch (error: any) {
      console.error('Registration failed:', error);
      const msg = error.message || 'Registration failed. Please try again.';
      setError(msg);
      return {
        success: false,
        message: msg,
        errorType: 'other'
      };
    }
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      return {
        success: true,
        message: 'Password reset instructions have been sent to your email.',
      };
    } catch (error: any) {
      console.error('Password reset failed:', error);
      const msg = error.message || 'Failed to send password reset instructions. Please try again.';
      setError(msg);
      return {
        success: false,
        message: msg,
      };
    }
  };

  /**
   * Logs out the current user
   * Clears authentication state and removes user data from storage
   */
  const logout = async () => {
    setError(null);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      await supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          console.log('Session still exists, forcing clear');
          document.cookie.split(';').forEach(function (c) {
            document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
          });
        }
      });
      // Navigate to home handled by calling component
    } catch (error: any) {
      console.error('Logout failed:', error);
      setError(error.message || 'Logout failed');
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      // Navigate to home handled by calling component
    }
  };

  const demoLogin = async () => {
    setError(null);
    const mockUser: CustomUser = {
      id: 'demo-user-123',
      aud: 'authenticated',
      role: 'farmer',
      email: 'free@demo.com',
      phone: '',
      created_at: new Date().toISOString(),
      confirmed_at: new Date().toISOString(),
      last_sign_in_at: new Date().toISOString(),
      app_metadata: { provider: 'email' },
      user_metadata: { name: 'Demo Farmer', role: 'farmer' },
      identities: [],
      name: 'Demo Farmer',
      email_confirmed_at: new Date().toISOString(),
      phone_confirmed_at: null,
    };

    setUser(mockUser);
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify(mockUser));
  };

  const value = React.useMemo(() => ({
    isAuthenticated,
    user,
    loading,
    error,
    clearError,
    login,
    register,
    signInWithProvider,
    resetPassword,
    logout,
    demoLogin
  }), [isAuthenticated, user, loading, error]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access the authentication context
 * @returns Authentication context with user state and auth functions
 * @throws Error if used outside of AuthProvider
 */
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
